/**
 * The device-remembered identity (onboarding model R4). After a sign-in the
 * server stores a hint in an HttpOnly cookie (who signed in, and whether that
 * identity has a sign-in passkey), so the next login page can open at that
 * identity's passkey step instead of asking for an email first.
 *
 * The hint authorizes nothing. It holds no session or token; the passkey
 * ceremony still proves who is signing in. Server side:
 * judge-api/cmd/server/cmd/handlers_remembered_identity.go.
 */
import { csrfFetch } from './csrf';
import { createLogger } from './logger';
const log = createLogger('remembered-identity');
const REMEMBERED_IDENTITY_URL = '/api/auth/remembered-identity';
const REMEMBER_DEVICE_URL = '/api/auth/remember-device';
/** The Kratos session id this device was last remembered for (one POST per sign-in). */
const REMEMBERED_SESSION_KEY = 'judge-remembered-session';
export interface RememberedIdentity {
  email: string;
  /** True when the identity had a sign-in passkey at its last sign-in. */
  passkey: boolean;
}

/**
 * localStorage can throw (private mode, blocked storage). The marker it holds
 * only saves a request, so a failure is logged and otherwise ignored.
 */
function storage(op: (s: Storage) => string | null | void): string | null {
  try {
    return op(localStorage) ?? null;
  } catch (err) {
    log.debug('localStorage unavailable', {
      error: String(err)
    });
    return null;
  }
}

/**
 * The hint this browser holds, or null. Any failure is "not remembered": the
 * login page then shows the email-first screen, which is always correct.
 */
export async function fetchRememberedIdentity(): Promise<RememberedIdentity | null> {
  try {
    const resp = await fetch(REMEMBERED_IDENTITY_URL, {
      credentials: 'include',
      cache: 'no-store'
    });
    if (!resp.ok) return null;
    const body = (await resp.json()) as {
      remembered?: unknown;
      email?: unknown;
      passkey?: unknown;
    };
    if (body?.remembered !== true || typeof body.email !== 'string' || body.email === '') return null;
    return {
      email: body.email,
      passkey: body.passkey === true
    };
  } catch (err) {
    log.debug('remembered identity unavailable', {
      error: String(err)
    });
    return null;
  }
}

/**
 * Forget this browser's hint ("Not you? Use a different account"). Resolves
 * either way; a failed forget is logged, and the caller still shows the
 * email-first screen.
 */
export async function forgetRememberedIdentity(): Promise<void> {
  storage(s => s.removeItem(REMEMBERED_SESSION_KEY));
  try {
    const resp = await fetch(REMEMBERED_IDENTITY_URL, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!resp.ok) log.warn('forget remembered identity failed', {
      status: resp.status
    });
  } catch (err) {
    log.warn('forget remembered identity failed', {
      error: String(err)
    });
  }
}

/**
 * Record the signed-in identity on this device, once per sign-in session.
 * Best effort: a failure only means the next login starts at the email page.
 */
export async function rememberDeviceForSession(sessionId: string): Promise<void> {
  if (storage(s => s.getItem(REMEMBERED_SESSION_KEY)) === sessionId) return;
  try {
    const resp = await csrfFetch(REMEMBER_DEVICE_URL, {
      method: 'POST'
    });
    if (!resp.ok) {
      log.warn('remember device failed', {
        status: resp.status
      });
      return;
    }
    storage(s => s.setItem(REMEMBERED_SESSION_KEY, sessionId));
  } catch (err) {
    log.warn('remember device failed', {
      error: String(err)
    });
  }
}